/**
 * FocusEditor — the stage: `[FocusPanel | the preview, centred]`.
 *
 * The one behaviour that matters: **the page never scrolls**. The stage is
 * handed the exact height it may occupy (`stageHeight`, the host's
 * `calc(100vh − chrome)`) and the preview is sized to FIT it — capped both
 * by that height and by the width the preview column actually has
 * (`width × 16/9`, the term that stops a tall viewport from pushing a 9:16
 * frame out sideways). Everything that can overflow scrolls INSIDE the
 * panel column, never on the document.
 *
 * The height is handed BACK to the caller (`render(previewHeight)`) instead
 * of being applied here: only the caller knows what it is mounting (a
 * Remotion `<Player>` wants explicit pixel dimensions, and its canvas
 * overlay has to match them exactly), so the stage measures and the caller
 * sizes. There is no `children` prop — one way to pass the preview, so the
 * measured height can never be silently ignored.
 */
import { useLayoutEffect, useRef, useState } from "react";

import { FocusPanel, type FocusPanelProps } from "./FocusPanel.tsx";

/** Room reserved under the preview for ▶ / time / the current cue line. */
const TRANSPORT_H = 64;
/** The gap between the preview and the transport — part of the budget, or
 *  the column is `GAP` px taller than `stageHeight` and the PAGE scrolls,
 *  which is the one thing this stage exists to prevent. */
const STACK_GAP = 10;

export interface FocusEditorProps {
  panel: FocusPanelProps;
  /** px the stage may occupy — the caller computes `calc(100vh − chrome)`. */
  stageHeight: number;
  /** Given the height the stage computed, return the preview (Player +
   *  overlay) sized to exactly that. */
  render: (previewHeight: number) => React.ReactNode;
  /** ▶ time + (Studio) the current cue as one clickable line. */
  transport: React.ReactNode;
}

export function FocusEditor({ panel, stageHeight, render, transport }: FocusEditorProps): React.JSX.Element {
  const colRef = useRef<HTMLDivElement | null>(null);
  const [colWidth, setColWidth] = useState(0);

  // The preview column is `flex: 1 1 0` with `minWidth: 0`, so its width is
  // distributed space, never content-driven — measuring it cannot feed back
  // into the size we compute from it.
  useLayoutEffect(() => {
    const el = colRef.current;
    if (!el) return;
    setColWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setColWidth(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const previewHeight = Math.max(0, Math.min(stageHeight - TRANSPORT_H - STACK_GAP, Math.floor((colWidth * 16) / 9)));

  return (
    <div style={{ display: "flex", gap: 16, height: stageHeight, alignItems: "stretch", minHeight: 0 }}>
      {/* Panel column: fixed width, clipped — the panel scrolls internally. */}
      <div style={{ display: "flex", flexShrink: 0, minHeight: 0, overflow: "hidden" }}>
        <FocusPanel {...panel} />
      </div>
      {/* Preview column: whatever is left, preview centred in it. */}
      <div ref={colRef} style={{ flex: 1, minWidth: 0, minHeight: 0, display: "grid", placeItems: "center", overflow: "hidden" }}>
        <div style={{ display: "grid", gap: STACK_GAP, justifyItems: "center", minWidth: 0 }}>
          {render(previewHeight)}
          {transport}
        </div>
      </div>
    </div>
  );
}
